import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Loader } from 'components/Shared/Loader'
import { SuccessDialog } from 'components/Shared/Dialogs/SuccessDialog'
import { triggerFormSubmit } from 'utils/utils'
import { TitleBar } from 'components/Shared/CrudComponents/TitleBar'
import styles from 'components/Shared/CrudComponents/SimpleForm.module.css'
import { useErrorHandling } from 'context/errorHandling'
import { HTTP_404_ERROR, HTTP_422_ERROR } from 'Constants'
import ApiError from 'constants/ApiError'
import { editContractor, fetchContractor } from 'services/ContractorService'
import { ContractorFormFields, ContractorSchema } from 'components/Contractors/ContractorFormFields'
import { FormikForm } from 'components/Shared/FormikForm'
import { useNavigatePrevent } from 'context/navigatePrevent'
import Module from 'constants/customFieldModules'
import {ErrorDialog} from "../../Shared/Dialogs";
import {extendContractorLock, setContractorLock} from "../../../services";

export function EditContractorForm (props) {
  const [editedDialogOpen, setEditedDialogOpen] = useState(false)
  const [contractor, setContractor] = useState(null)
  const [lockId, setLockId] = useState(null)
  const [lockedError, setLockedError] = useState(null)
  const [lockExtendingCount, setLockExtendingCount] = useState(0)

  const { handleError } = useErrorHandling()
  const { t } = useTranslation()
  const { setNavigationUnlocked } = useNavigatePrevent()

  const editContractorFormRef = useRef()

  const initialValues = {
    ...contractor,
    filesIds: contractor?.files.map(file => file.id)
  }

  useEffect(() => {
      fetchContractor(props.match.params.contractorId)
        .then(setContractor)
        .catch(error =>
          handleError(error, {
            [HTTP_404_ERROR]: 'notifications.error.contractorNotFound'
          }).then(() => {
            setNavigationUnlocked(history => history.push('/contractors'))
          })
        )
    }, [props.match.params.contractorId, handleError, setNavigationUnlocked]
  )

  useEffect(() => {
      if (props.match.params.contractorId && lockExtendingCount === 0) {
        setContractorLock(props.match.params.contractorId, true).then((response) => {
          setLockId(response.LockId)
          if(lockExtendingCount === 0){
            setTimeout(() => {
              setLockExtendingCount(lockExtendingCount + 1)
            }, 60000)
          }

        },(error) => {
          const errResp = error.response.data
          if(errResp.errorCode === 'RESOURCE_LOCKED'){
            setLockedError(errResp)
          }
          else{
            handleError(error)
          }
        })
      }

    }, [props.match.params.contractorId, setLockId, setLockExtendingCount, lockExtendingCount, handleError]
  )

  useEffect(() => {
    let isComponentMounted = true;
    if (lockId && lockExtendingCount > 0) {
      extendContractorLock(props.match.params.contractorId, lockId)
        .then((response) => {
          setLockId(response.LockId)
          if (isComponentMounted) {
            setTimeout(() => {
              if (isComponentMounted) {
                setLockExtendingCount(lockExtendingCount + 1)
              }
            }, 60000)
          }
        },(error) => {
          const errResp = error.response?.data
          if(errResp.errorCode === 'RESOURCE_LOCKED'){
            setLockedError(errResp)
          }
          else{
            handleError(error)
          }
        })
    }

    return () => {
      isComponentMounted = false;
    };
  }, [lockExtendingCount, setLockExtendingCount, lockId, props.match.params.contractorId, handleError]);

  const onContractorEdit = useCallback((values, onSuccess) =>
    editContractor({
      id: contractor.id,
      etag: contractor.etag,
      lockId,
      ...values
    }).then(() => onSuccess(contractor.id)
      .then(() => setEditedDialogOpen(true))
      .catch(handleError)
    )
      .catch(error => {
        const errorCode = error?.response?.data?.errorCode
        if(errorCode === 'RESOURCE_LOCKED'){
          setLockedError(error?.response?.data)
        }else{
          handleError(error, {
            [HTTP_404_ERROR]: 'notifications.error.contractorNotFound',
            [HTTP_422_ERROR]: errorCode === 'CONTRACTOR_NIP_CONFLICT' ?
              'notifications.error.contractorNipNumberAlreadyExists' :
              errorCode === 'CONTRACTOR_CONTACT_EMAIL_CONFLICT' ?
                'notifications.error.contractorContactEmailAlreadyExists' :
                errorCode === 'CONTRACTOR_NAME_CONFLICT' ?
                  'notifications.error.contractorFullNameAlreadyExists' :
                  ApiError.UnprocessableEntity
          })
        }
      }), [contractor, handleError,lockId])

  function handleSuccessDialogClose () {
    setEditedDialogOpen(false)
    setNavigationUnlocked(history => history.push('/contractors'))
  }

  return <div>
    <TitleBar
      buttonLabel={t('contractor.form.submit')}
      onButtonClick={triggerFormSubmit(editContractorFormRef)}
      title={t('contractor.form.editTitle')}/>
    <SuccessDialog open={editedDialogOpen}
                   onClose={handleSuccessDialogClose}
                   content={t('notifications.success.contractorEdited')}/>
    <ErrorDialog
      open={lockedError}
      onClose={() => {
        setNavigationUnlocked(history => history.push('/contractors'))
        setEditedDialogOpen(null)
      }}
      content={<ContractorLockedInfoMessage error={lockedError}/>}
    />
    {contractor && lockId ? <div className={styles.containerRow}>
        <FormikForm
          innerRef={editContractorFormRef}
          enableReinitialize
          initialValues={initialValues}
          lockId={lockId}
          validationSchema={ContractorSchema}
          onSubmit={onContractorEdit}
          customFields={{
            module: Module.Contractors,
            entityId: contractor?.id
          }}>
          {() => (
            <ContractorFormFields initialFiles={contractor.files}/>
          )}
        </FormikForm>
      </div>
      :
      (lockId && <Loader/>)
    }
  </div>
}

export const ContractorLockedInfoMessage = ({error}) => {
  const {t} = useTranslation()
  return (
    <>
      <p>{t('notifications.error.contractorEditingLocked', {editingUser: error.data.user})}</p>
    </>
  )
}